import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'wouter'
import { MVP } from '../../mvp'
import { ROUTES } from '../../routes'
import useDossierExposant from '../../hooks/useDossierExposant'
import { avecAlpha } from '../../lib/couleurs'
import { ORANGE, TEAL, VERT, eur, fmtDateLongue, fmtHoraire, offreEmoji, offreNom, prixMarche, eurOuASaisir } from './marcheProducer'

/**
 * Feuille « Confirmer mon stand pour ce jour » (proto v7.2.1, coeur du sujet 3).
 * Pre-coche le stand de reference (ou la composition deja confirmee), le
 * producteur decoche ce qu'il n'apporte pas, puis confirme — c'est cette
 * confirmation DATEE qui publie le stand cote client.
 */
function ConfirmStandSheet({ open, marche, offres, initialSelected, dejaConfirme, saving, onClose, onConfirm }) {
  const [selected, setSelected] = useState(() => new Set(initialSelected || []))
  const [, setLocation] = useLocation()
  const dossier = useDossierExposant({ enabled: MVP.DOSSIER_EXPOSANT })

  useEffect(() => {
    if (open) setSelected(new Set(initialSelected || []))
    // initialSelected volontairement fige a l'ouverture
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open || !marche) return null

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const nbSel = selected.size

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end"
      role="dialog"
      aria-modal="true"
      style={{ background: 'rgba(4,25,24,0.5)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full flex flex-col panel-slide-up"
        style={{ background: 'rgb(var(--c-creme))', borderRadius: '28px 28px 0 0', maxHeight: '88dvh' }}
      >
        <div className="px-5 pt-4 pb-3" style={{ borderBottom: '1px solid rgba(7,62,61,0.08)' }}>
          <div className="w-[42px] h-1 rounded-full mx-auto mb-4" style={{ background: 'rgba(7,62,61,0.15)' }} />
          <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: 2, color: ORANGE }}>
            {dejaConfirme ? 'Changer mes produits' : 'Mes produits ce jour-là'}
          </p>
          <p className="font-serif text-[22px] mt-0.5" style={{ color: VERT }}>
            {marche.nom || marche.lieu}
          </p>
          <p className="text-[12.5px] mt-0.5 text-ink/60">
            📅 {fmtDateLongue(marche.date)}
            {fmtHoraire(marche) ? ` · 🕖 ${fmtHoraire(marche)}` : ''}
          </p>
        </div>

        <div className="px-5 py-3.5 overflow-y-auto flex-1 overscroll-contain">
          <p className="text-[12.5px] leading-relaxed mb-3.5 text-ink/65">
            🦊 Voici vos produits habituels. <strong>Décochez ce que vous n'apporterez pas ce jour-là</strong> — seuls
            les produits cochés seront visibles par les clients pour cet évènement.
          </p>
          {/* v7.5 — rappel, JAMAIS bloquant (D2) : l'inscription reste possible */}
          {MVP.DOSSIER_EXPOSANT && dossier?.niveau === 'INCOMPLET' && (
            <div className="rounded-2xl flex gap-[11px] items-start mb-3.5"
              style={{ padding: '12px 14px', background: avecAlpha(ORANGE, 0.1), border: `1px solid ${avecAlpha(ORANGE, 0.3)}` }}>
              <span className="text-lg">📋</span>
              <div className="flex-1">
                <p className="text-[12.5px] leading-normal" style={{ color: VERT }}>
                  <strong>Votre dossier exposant est incomplet.</strong> Vous pouvez vous inscrire, mais la mairie l'exige pour le marché.
                </p>
                <button onClick={() => { onClose(); setLocation(ROUTES.compte.dossier) }}
                  className="mt-1.5 text-[12.5px] font-extrabold" style={{ color: '#b4551f' }}>
                  Compléter mon dossier ›
                </button>
              </div>
            </div>
          )}
          {offres.length === 0 ? (
            <p className="text-[12.5px] text-center py-5" style={{ color: '#d9534f' }}>
              Aucun produit au canal Évènements. Ajoutez d'abord vos produits dans « Offres ».
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {offres.map((o) => {
                const on = selected.has(o.id)
                return (
                  <button
                    key={o.id}
                    onClick={() => toggle(o.id)}
                    className="rounded-2xl flex items-center gap-3 text-left"
                    style={{
                      padding: '12px 14px',
                      border: on ? `1.5px solid ${TEAL}` : '1px solid rgba(7,62,61,0.1)',
                      background: on ? 'rgba(20,184,166,0.08)' : 'rgba(255,255,255,0.5)',
                    }}
                  >
                    <span className="text-2xl flex-shrink-0" style={{ opacity: on ? 1 : 0.5 }}>{offreEmoji(o)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold" style={{ color: VERT }}>{offreNom(o)}</p>
                      <p className="text-[11.5px] text-ink/55">
                        {eurOuASaisir(prixMarche(o))}{o.prixUnit ? ` / ${o.prixUnit}` : ''}
                      </p>
                    </div>
                    <span
                      className="w-6 h-6 rounded-[7px] flex-shrink-0 flex items-center justify-center text-white text-sm font-extrabold"
                      style={{ background: on ? TEAL : 'transparent', border: on ? 'none' : '2px solid rgba(7,62,61,0.25)' }}
                    >
                      {on ? '✓' : ''}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="px-5 pt-3 pb-6" style={{ borderTop: '1px solid rgba(7,62,61,0.08)' }}>
          <button
            disabled={nbSel === 0 || saving}
            onClick={() => onConfirm([...selected])}
            className="w-full rounded-2xl text-[15px] font-extrabold disabled:opacity-100"
            style={{
              padding: '15px 0',
              border: 'none',
              background: nbSel && !saving ? VERT : 'rgba(7,62,61,0.18)',
              color: 'rgb(var(--c-creme))',
            }}
          >
            {saving
              ? 'Confirmation…'
              : dejaConfirme
                ? `Mettre à jour (${nbSel} produit${nbSel > 1 ? 's' : ''})`
                : `J'apporte ces produits (${nbSel})`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default ConfirmStandSheet
